import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Grid,
  MenuItem,
  Box,
  Chip,
  TextField,
  Autocomplete,
  InputAdornment,
  Paper,
} from "@mui/material";
import {
  Refresh,
  LocalParking,
  DirectionsCar,
  Business,
  Search,
  AccessTime,
  Group,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  ConfirmationNumber,
} from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";

const HeaderVipCame = ({
  groups = [],
  selectedGroup,
  onSelectGroup,
  onRefresh,
  loading = false,
  stats = {},
  cuposInfo = null,
  selectedGroupQuota = 0,
  occupancyKnown = true,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [filteredGroups, setFilteredGroups] = useState(groups);

  useEffect(() => {
    if (!searchInput.trim()) {
      setFilteredGroups(groups);
    } else {
      const query = searchInput.toLowerCase();
      const filtered = groups.filter(
        (group) =>
          group.name.toLowerCase().includes(query) ||
          group.idgroup.toString().includes(query) ||
          (group.name && group.name.toLowerCase().includes(query))
      );
      setFilteredGroups(filtered);
    }
  }, [searchInput, groups]);

  const handleGroupSelect = (event, newValue) => {
    if (newValue && onSelectGroup) {
      onSelectGroup(newValue);
    } else if (onSelectGroup && newValue === null) {
      onSelectGroup(null);
    }
    setSearchInput(newValue ? newValue.name : "");
  };

  const handleInputChange = (event, newInputValue) => {
    setSearchInput(newInputValue);
  };

  const formatTime = () => {
    const now = new Date();
    return now.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  // Determinar color según disponibilidad de cupos
  const getCuposColor = (disponibles, total) => {
    const porcentaje = (disponibles / total) * 100;
    if (porcentaje <= 20) return "error";
    if (porcentaje <= 50) return "warning";
    return "success";
  };

  // Determinar ícono según disponibilidad
  const getCuposIcon = (disponibles, total) => {
    const porcentaje = (disponibles / total) * 100;
    if (porcentaje <= 20) return <TrendingDown sx={{ color: "#f44336" }} />;
    if (porcentaje >= 80) return <TrendingUp sx={{ color: "#4caf50" }} />;
    return <TrendingFlat sx={{ color: "#ff9800" }} />;
  };

  // Calcular porcentaje de uso del grupo seleccionado
  const getGroupUsagePercentage = () => {
    if (selectedGroupQuota === 0) return 0;
    const used = stats.parkingVehicles || 0;
    return Math.round((used / selectedGroupQuota) * 100);
  };

  const groupUsagePercentage = getGroupUsagePercentage();

  return (
    <MDBox position="relative" mb={4}>
      <Card
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <MDBox
          p={3}
          sx={{
            backgroundColor: "#1a1a2e",
            background: "linear-gradient(135deg, #16213e 0%, #0f3460 100%)",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={7}>
              <MDBox display="flex" alignItems="center" gap={2}>
                <Business sx={{ color: "#4cc9f0", fontSize: 32 }} />
                <Box>
                  <MDTypography
                    variant="h5"
                    color="white"
                    fontWeight="bold"
                    sx={{
                      lineHeight: 1.2,
                      letterSpacing: "0.5px",
                    }}
                  >
                    VIP Came - Control de Vehículos
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="rgba(255,255,255,0.7)"
                    sx={{
                      mt: 0.5,
                      display: "block",
                      fontWeight: 400,
                    }}
                  >
                    Gestión de grupos y vehículos registrados
                  </MDTypography>
                </Box>
              </MDBox>
            </Grid>

            <Grid item xs={12} md={5}>
              <MDBox display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                <MDButton
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={onRefresh}
                  disabled={loading}
                  startIcon={<Refresh />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: "#4361ee",
                    "&:hover": {
                      backgroundColor: "#3a56d4",
                      boxShadow: "0 4px 12px rgba(67, 97, 238, 0.3)",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#2d3a8c",
                      color: "rgba(255,255,255,0.5)",
                    },
                  }}
                >
                  {loading ? "Actualizando..." : "Actualizar"}
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox p={3}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <MDTypography variant="button" color="text.secondary" fontWeight="medium" mb={1}>
                <Search fontSize="small" sx={{ mr: 1, verticalAlign: "middle" }} />
                Buscar y Seleccionar Grupo VIP
              </MDTypography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "2px solid",
                  borderColor: "#e0e0e0",
                  "&:hover": {
                    borderColor: "#4361ee",
                  },
                  transition: "border-color 0.2s ease",
                }}
              >
                <Autocomplete
                  id="group-search-select"
                  options={filteredGroups}
                  getOptionLabel={(option) => option?.name || ""}
                  value={selectedGroup || null}
                  onChange={handleGroupSelect}
                  inputValue={searchInput}
                  onInputChange={handleInputChange}
                  loading={loading}
                  loadingText="Cargando grupos..."
                  noOptionsText="No se encontraron grupos"
                  disabled={loading}
                  clearOnBlur={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Escribe para buscar grupos..."
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: "#4361ee" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "none",
                          },
                          "&:hover fieldset": {
                            border: "none",
                          },
                          "&.Mui-focused fieldset": {
                            border: "none",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem {...props} sx={{ py: 1.5 }}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <Box>
                          <MDTypography variant="body2" fontWeight="medium">
                            {option.name}
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary">
                            ID: {option.idgroup} | Cupos: {option.quotas || 0}
                          </MDTypography>
                        </Box>
                        {selectedGroup && option.idgroup === selectedGroup.idgroup && (
                          <Chip
                            label="Seleccionado"
                            size="small"
                            sx={{
                              backgroundColor: "#4361ee",
                              color: "white",
                              fontWeight: 500,
                              fontSize: "0.7rem",
                            }}
                          />
                        )}
                      </MDBox>
                    </MenuItem>
                  )}
                  PaperComponent={({ children }) => (
                    <Paper
                      sx={{
                        borderRadius: "8px",
                        marginTop: "8px",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      {children}
                    </Paper>
                  )}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              {selectedGroup ? (
                <MDBox
                  p={2}
                  borderRadius="8px"
                  sx={{
                    backgroundColor: "#f5f7fa",
                    border: "1px solid",
                    borderColor: "#4361ee",
                    boxShadow: "0 2px 8px rgba(67, 97, 238, 0.1)",
                  }}
                >
                  <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item xs={8}>
                      <MDBox display="flex" alignItems="center" gap={1.5}>
                        <Group sx={{ color: "#4361ee" }} />
                        <Box>
                          <MDTypography variant="h6" color="#1a1a2e" fontWeight="bold">
                            {selectedGroup.name || "Sin nombre"}
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary">
                            ID: {selectedGroup.idgroup || "N/A"} | Cupos: {selectedGroupQuota}
                          </MDTypography>
                        </Box>
                      </MDBox>
                    </Grid>
                    <Grid item xs={4}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        align="right"
                        sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
                      >
                        <AccessTime fontSize="inherit" sx={{ mr: 0.5 }} />
                        {formatTime()}
                      </MDTypography>
                    </Grid>
                  </Grid>
                </MDBox>
              ) : (
                <MDBox
                  p={3}
                  textAlign="center"
                  borderRadius="8px"
                  sx={{
                    backgroundColor: "#f8f9fa",
                    border: "2px dashed",
                    borderColor: "#dee2e6",
                  }}
                >
                  <MDTypography variant="body2" color="#6c757d">
                    <Search sx={{ mr: 1, verticalAlign: "middle", fontSize: 20 }} />
                    Seleccione o busque un grupo para ver estadísticas
                  </MDTypography>
                </MDBox>
              )}
            </Grid>
          </Grid>

          {/* Tarjeta de Cupos del Grupo Seleccionado */}
          {selectedGroup && selectedGroupQuota > 0 && occupancyKnown && (
            <MDBox mt={3}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "12px",
                  borderColor:
                    (stats.parkingVehicles || 0) >= selectedGroupQuota
                      ? "#f44336"
                      : groupUsagePercentage >= 80
                      ? "#ff9800"
                      : "#4caf50",
                  backgroundColor: "background.paper",
                  overflow: "hidden",
                }}
              >
                <MDBox
                  p={2}
                  sx={{
                    backgroundColor:
                      (stats.parkingVehicles || 0) >= selectedGroupQuota
                        ? "#ffebee"
                        : groupUsagePercentage >= 80
                        ? "#fff3e0"
                        : "#e8f5e9",
                    borderBottom: `3px solid ${
                      (stats.parkingVehicles || 0) >= selectedGroupQuota
                        ? "#f44336"
                        : groupUsagePercentage >= 80
                        ? "#ff9800"
                        : "#4caf50"
                    }`,
                  }}
                >
                  <MDTypography variant="h6" fontWeight="bold" color="text.primary">
                    <ConfirmationNumber sx={{ mr: 1, verticalAlign: "middle" }} />
                    Cupos del Grupo: {selectedGroup.name}
                  </MDTypography>
                </MDBox>

                <Grid container spacing={3} p={3}>
                  <Grid item xs={12} md={4}>
                    <MDBox textAlign="center">
                      <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                        Cupos Asignados
                      </MDTypography>
                      <MDTypography variant="h3" fontWeight="bold" color="text.primary">
                        {selectedGroupQuota}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        espacios totales
                      </MDTypography>
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDBox textAlign="center">
                      <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                        Vehículos en Parqueadero
                      </MDTypography>
                      <MDTypography
                        variant="h3"
                        fontWeight="bold"
                        color={
                          (stats.parkingVehicles || 0) >= selectedGroupQuota ? "error" : "warning"
                        }
                      >
                        {stats.parkingVehicles || 0}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        vehículos actualmente
                      </MDTypography>
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDBox textAlign="center">
                      <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
                        <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                          Cupos Disponibles
                        </MDTypography>
                        {getCuposIcon(
                          selectedGroupQuota - (stats.parkingVehicles || 0),
                          selectedGroupQuota
                        )}
                      </MDBox>
                      <MDTypography
                        variant="h3"
                        fontWeight="bold"
                        color={
                          (stats.parkingVehicles || 0) >= selectedGroupQuota
                            ? "error"
                            : groupUsagePercentage >= 80
                            ? "warning"
                            : "success"
                        }
                      >
                        {Math.max(0, selectedGroupQuota - (stats.parkingVehicles || 0))}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        {selectedGroupQuota - (stats.parkingVehicles || 0) === 0
                          ? "¡Cupos agotados!"
                          : selectedGroupQuota - (stats.parkingVehicles || 0) <=
                            Math.ceil(selectedGroupQuota * 0.2)
                          ? "¡Espacios libres!"
                          : "espacios libres"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>

                {/* Barra de progreso */}
                <MDBox px={3} pb={3}>
                  <MDBox
                    sx={{
                      width: "100%",
                      height: "8px",
                      backgroundColor: "#e0e0e0",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    <MDBox
                      sx={{
                        width: `${Math.min(100, groupUsagePercentage)}%`,
                        height: "100%",
                        backgroundColor:
                          (stats.parkingVehicles || 0) >= selectedGroupQuota
                            ? "#f44336"
                            : groupUsagePercentage >= 80
                            ? "#ff9800"
                            : "#4caf50",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" mt={1}>
                    <MDTypography variant="caption" color="text.secondary">
                      0%
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      {groupUsagePercentage}% ocupado
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      100%
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          )}

          {/* Tarjeta de Cupos Generales - Mostrar solo en modo "Todos los Vehículos" */}
          {!selectedGroup && occupancyKnown && cuposInfo && cuposInfo.cupo_total > 0 && (
            <MDBox mt={3}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "12px",
                  borderColor: getCuposColor(cuposInfo.cupos_disponibles, cuposInfo.cupo_total),
                  backgroundColor: "background.paper",
                  overflow: "hidden",
                }}
              >
                <MDBox
                  p={2}
                  sx={{
                    backgroundColor: `${getCuposColor(
                      cuposInfo.cupos_disponibles,
                      cuposInfo.cupo_total
                    )}.lighter`,
                    borderBottom: `3px solid ${getCuposColor(
                      cuposInfo.cupos_disponibles,
                      cuposInfo.cupo_total
                    )}.main`,
                  }}
                >
                  <MDTypography variant="h6" fontWeight="bold" color="text.primary">
                    <LocalParking sx={{ mr: 1, verticalAlign: "middle" }} />
                    Estado General del Parqueadero
                  </MDTypography>
                </MDBox>

                <Grid container spacing={3} p={3}>
                  <Grid item xs={12} md={4}>
                    <MDBox textAlign="center">
                      <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                        Cupos Totales
                      </MDTypography>
                      <MDTypography variant="h3" fontWeight="bold" color="text.primary">
                        {cuposInfo.cupo_total}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        espacios disponibles
                      </MDTypography>
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDBox textAlign="center">
                      <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                        Cupos Usados
                      </MDTypography>
                      <MDTypography
                        variant="h3"
                        fontWeight="bold"
                        color={cuposInfo.cupos_usados >= cuposInfo.cupo_total ? "error" : "warning"}
                      >
                        {cuposInfo.cupos_usados}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        vehículos actualmente
                      </MDTypography>
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDBox textAlign="center">
                      <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
                        <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                          Cupos Disponibles
                        </MDTypography>
                        {getCuposIcon(cuposInfo.cupos_disponibles, cuposInfo.cupo_total)}
                      </MDBox>
                      <MDTypography
                        variant="h3"
                        fontWeight="bold"
                        color={getCuposColor(cuposInfo.cupos_disponibles, cuposInfo.cupo_total)}
                      >
                        {cuposInfo.cupos_disponibles}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        {cuposInfo.cupos_disponibles === 0
                          ? "¡Parqueadero lleno!"
                          : cuposInfo.cupos_disponibles <= Math.ceil(cuposInfo.cupo_total * 0.2)
                          ? "espacios libres"
                          : "espacios libres"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>

                {/* Barra de progreso */}
                <MDBox px={3} pb={3}>
                  <MDBox
                    sx={{
                      width: "100%",
                      height: "8px",
                      backgroundColor: "#e0e0e0",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    <MDBox
                      sx={{
                        width: `${(cuposInfo.cupos_usados / cuposInfo.cupo_total) * 100}%`,
                        height: "100%",
                        backgroundColor:
                          cuposInfo.cupos_usados >= cuposInfo.cupo_total
                            ? "#f44336"
                            : cuposInfo.cupos_disponibles <= Math.ceil(cuposInfo.cupo_total * 0.2)
                            ? "#ff9800"
                            : "#4caf50",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" mt={1}>
                    <MDTypography variant="caption" color="text.secondary">
                      0%
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      {Math.round((cuposInfo.cupos_usados / cuposInfo.cupo_total) * 100)}% ocupado
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      100%
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          )}

          {/* Estadísticas del grupo seleccionado */}
          {selectedGroup && (
            <MDBox mt={3} pt={2} borderTop="1px solid #e0e0e0">
              <MDBox mt={2}>
                <MDTypography variant="caption" color="#6c757d" sx={{ fontStyle: "italic" }}>
                  <strong>Nota:</strong> Los datos reflejan el estado actual del grupo seleccionado.
                  Los vehículos en parqueadero incluyen aquellos que están actualmente dentro del
                  comercio.
                </MDTypography>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
};

HeaderVipCame.defaultProps = {
  groups: [],
  selectedGroup: null,
  onSelectGroup: null,
  onRefresh: null,
  loading: false,
  stats: {},
  cuposInfo: null,
  selectedGroupQuota: 0,
};

HeaderVipCame.propTypes = {
  groups: PropTypes.array,
  selectedGroup: PropTypes.object,
  onSelectGroup: PropTypes.func,
  onRefresh: PropTypes.func,
  loading: PropTypes.bool,
  stats: PropTypes.object,
  cuposInfo: PropTypes.shape({
    cupo_total: PropTypes.number,
    cupos_usados: PropTypes.number,
    cupos_disponibles: PropTypes.number,
  }),
  selectedGroupQuota: PropTypes.number,
  occupancyKnown: PropTypes.bool,
};

export default HeaderVipCame;
